import { useState } from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { 
  Search, 
  Plus, 
  MoreVertical, 
  MapPin, 
  Circle, 
  Hexagon,
  ExternalLink,
  PlayCircle,
  BookOpen
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useGeofences } from '@/hooks/use-geofences';
import { GeofenceForm } from './GeofenceForm';

interface GeofenceSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  companyId?: string;
  onViewOnMap?: (geofenceId: string) => void;
  onCenterMap?: (position: { lat: number; lng: number }) => void;
  onEnableDrawing?: (type: 'circle' | 'polygon') => void;
  drawnGeofence?: any;
}

export function GeofenceSheet({ 
  open, 
  onOpenChange, 
  companyId,
  onViewOnMap,
  onCenterMap,
  onEnableDrawing,
  drawnGeofence,
}: GeofenceSheetProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [creatingMode, setCreatingMode] = useState(false);
  const [editingGeofenceId, setEditingGeofenceId] = useState<string | null>(null);
  
  const { 
    geofences, 
    isLoading, 
    deleteGeofence,
    isDeleting 
  } = useGeofences(companyId);

  const filteredGeofences = geofences.filter(g => 
    g.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleCreateNew = () => {
    setCreatingMode(true);
    setEditingGeofenceId(null);
  };

  const handleEdit = (geofenceId: string) => {
    setEditingGeofenceId(geofenceId);
    setCreatingMode(true);
  };

  const handleDelete = async (geofenceId: string) => {
    if (confirm('Tem certeza que deseja excluir esta geocerca?')) {
      deleteGeofence(geofenceId);
    }
  };

  const handleViewOnMap = (geofenceId: string) => {
    const geofence = geofences.find(g => g.id === geofenceId);
    if (!geofence) return;

    // Centralizar mapa na geocerca
    if (geofence.type === 'circle' && onCenterMap) {
      onCenterMap({
        lat: geofence.coordinates.lat,
        lng: geofence.coordinates.lng,
      });
    } else if (geofence.type === 'polygon' && onCenterMap) {
      // Calcular centro do polígono
      const coords = geofence.coordinates;
      const avgLat = coords.reduce((sum: number, p: any) => sum + p.lat, 0) / coords.length;
      const avgLng = coords.reduce((sum: number, p: any) => sum + p.lng, 0) / coords.length;
      onCenterMap({ lat: avgLat, lng: avgLng });
    }

    if (onViewOnMap) {
      onViewOnMap(geofenceId);
    }
  };

  const handleBackToList = () => {
    setCreatingMode(false);
    setEditingGeofenceId(null);
  };

  if (creatingMode || editingGeofenceId) {
  return (
    <Sheet 
      open={open}
      modal={false}
      onOpenChange={(newOpen) => {
        // Prevenir fechamento durante criação/edição de geocerca
        if (!newOpen && (creatingMode || editingGeofenceId)) {
          const confirm = window.confirm(
            'Tem certeza que deseja cancelar? O desenho será perdido.'
          );
          if (!confirm) return;
        }
        onOpenChange(newOpen);
      }}
    >
      <SheetContent 
        side="left" 
        className="w-[420px] p-0"
        style={{ left: 'var(--sidebar-width, 0px)' }}
        onInteractOutside={(e) => {
          // Prevenir fechamento ao clicar fora durante criação/edição
          if (creatingMode || editingGeofenceId) {
            e.preventDefault();
          }
        }}
        onEscapeKeyDown={(e) => {
          // Prevenir fechamento com Escape durante criação/edição
          if (creatingMode || editingGeofenceId) {
            e.preventDefault();
          }
        }}
      >
          <GeofenceForm
            companyId={companyId}
            geofenceId={editingGeofenceId}
            onBack={handleBackToList}
            onCenterMap={onCenterMap}
            onEnableDrawing={onEnableDrawing}
            drawnGeofence={drawnGeofence}
          />
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent 
        side="left" 
        className="w-[420px] p-0 flex flex-col"
        style={{ left: 'var(--sidebar-width, 0px)' }}
      >
        <SheetHeader className="p-6 pb-4">
          <div className="flex items-center justify-between">
            <SheetTitle className="text-xl">
              Cercas virtuais ({geofences.length})
            </SheetTitle>
          </div>
          
          {/* Links informativos */}
          <div className="flex gap-3 text-xs text-muted-foreground mt-3">
            <a href="#" className="flex items-center gap-1 hover:text-primary">
              <BookOpen className="h-3 w-3" />
              Como funciona?
            </a>
            <a href="#" className="flex items-center gap-1 hover:text-primary">
              <PlayCircle className="h-3 w-3" />
              Assista a um vídeo
            </a>
            <a href="#" className="flex items-center gap-1 hover:text-primary">
              <ExternalLink className="h-3 w-3" />
              Tour guiado
            </a>
          </div>
        </SheetHeader>

        {/* Botão Nova Cerca */}
        <div className="px-6 pb-4">
          <Button 
            onClick={handleCreateNew} 
            className="w-full"
            size="lg"
          >
            <Plus className="h-4 w-4 mr-2" />
            Nova Cerca
          </Button>
        </div>

        {/* Campo de busca */}
        <div className="px-6 pb-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Pesquisar"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9"
            />
          </div>
        </div>

        {/* Lista de geocercas */}
        <ScrollArea className="flex-1 px-6">
          {isLoading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : filteredGeofences.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              {searchTerm ? 'Nenhuma cerca encontrada' : 'Nenhuma cerca cadastrada'}
            </div>
          ) : (
            <div className="space-y-3 pb-6">
              {filteredGeofences.map((geofence) => (
                <div
                  key={geofence.id}
                  className="border rounded-lg p-4 hover:border-primary/50 transition-colors"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-2">
                        {geofence.type === 'circle' ? (
                          <Circle className="h-4 w-4 text-blue-600" />
                        ) : (
                          <Hexagon className="h-4 w-4 text-green-600" />
                        )}
                        <h4 className="font-medium truncate">{geofence.name}</h4>
                      </div>
                      
                      <div className="flex flex-wrap gap-2">
                        <Badge variant={geofence.active ? "default" : "secondary"}>
                          {geofence.active ? 'Ativa' : 'Inativa'}
                        </Badge>
                        <Badge variant="outline">
                          {geofence.type === 'circle' ? 'Círculo' : 'Polígono'}
                        </Badge>
                        {geofence.alert_on_entry && (
                          <Badge variant="outline" className="text-green-600">
                            Entrada
                          </Badge>
                        )}
                        {geofence.alert_on_exit && (
                          <Badge variant="outline" className="text-red-600">
                            Saída
                          </Badge>
                        )}
                      </div>
                    </div>

                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8">
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => handleViewOnMap(geofence.id)}>
                          <MapPin className="h-4 w-4 mr-2" />
                          Ver no mapa
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleEdit(geofence.id)}>
                          Editar
                        </DropdownMenuItem>
                        <DropdownMenuItem 
                          onClick={() => handleDelete(geofence.id)}
                          className="text-destructive"
                          disabled={isDeleting}
                        >
                          Excluir
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </div>
              ))}
            </div>
          )}
        </ScrollArea>

        {/* Paginação */}
        {filteredGeofences.length > 0 && (
          <div className="border-t p-4 text-sm text-muted-foreground">
            Mostrando {filteredGeofences.length} de {geofences.length} cerca(s)
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
